import React, { useEffect, Suspense, lazy, createElement } from 'react';
import { StickyTopBar } from './components/StickyTopBar';
import { HeroSection } from './components/HeroSection';
import { SocialProofBar } from './components/SocialProofBar';
// Lazy load below-fold sections to reduce initial bundle size
const ShortTestimonials = lazy(() => import('./components/ShortTestimonials').then(m => ({
  default: m.ShortTestimonials
})));
const PainPointsSection = lazy(() => import('./components/PainPointsSection').then(m => ({
  default: m.PainPointsSection
})));
const ProductShowcase = lazy(() => import('./components/ProductShowcase').then(m => ({
  default: m.ProductShowcase
})));
const TomosDetail = lazy(() => import('./components/TomosDetail').then(m => ({
  default: m.TomosDetail
})));
const IncludesSection = lazy(() => import('./components/IncludesSection').then(m => ({
  default: m.IncludesSection
})));
const BonusSection = lazy(() => import('./components/BonusSection').then(m => ({
  default: m.BonusSection
})));
const CountdownSection = lazy(() => import('./components/CountdownSection').then(m => ({
  default: m.CountdownSection
})));
const PricingSection = lazy(() => import('./components/PricingSection').then(m => ({
  default: m.PricingSection
})));
const FullTestimonials = lazy(() => import('./components/FullTestimonials').then(m => ({
  default: m.FullTestimonials
})));
const ForWhomSection = lazy(() => import('./components/ForWhomSection').then(m => ({
  default: m.ForWhomSection
})));
const GuaranteeSection = lazy(() => import('./components/GuaranteeSection').then(m => ({
  default: m.GuaranteeSection
})));
const FAQSection = lazy(() => import('./components/FAQSection').then(m => ({
  default: m.FAQSection
})));
const FinalCTASection = lazy(() => import('./components/FinalCTASection').then(m => ({
  default: m.FinalCTASection
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const SocialProofPopup = lazy(() => import('./components/SocialProofPopup').then(m => ({
  default: m.SocialProofPopup
})));
const META_PIXEL_ID = '1313291250496067';
function loadFontsAsync() {
  // Load Google Fonts asynchronously to avoid render-blocking
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&display=swap';
  link.media = 'print';
  link.onload = () => {
    link.media = 'all';
  };
  document.head.appendChild(link);
}
function loadMetaPixelDeferred() {
  if (typeof window === 'undefined' || (window as any).fbq) return;
  const f = window as any;
  const n = f.fbq = function (...args: any[]) {
    if (n.callMethod) {
      n.callMethod.apply(n, args);
    } else {
      n.queue.push(args);
    }
  };
  if (!f._fbq) f._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [] as any[];
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  (window as any).fbq('init', META_PIXEL_ID);
  (window as any).fbq('track', 'PageView');
  const noscript = document.createElement('noscript');
  const img = document.createElement('img');
  img.height = 1;
  img.width = 1;
  img.style.display = 'none';
  img.src = `https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.body.appendChild(noscript);
}
export function App() {
  useEffect(() => {
    // Preconnect to critical origins
    const preconnectCDN = document.createElement('link');
    preconnectCDN.rel = 'preconnect';
    preconnectCDN.href = 'https://cdn.magicpatterns.com';
    document.head.appendChild(preconnectCDN);
    const preconnectFonts = document.createElement('link');
    preconnectFonts.rel = 'preconnect';
    preconnectFonts.href = 'https://fonts.gstatic.com';
    preconnectFonts.crossOrigin = 'anonymous';
    document.head.appendChild(preconnectFonts);
    // SEO Meta Tags
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'Aprendé el oficio de refrigeración que más paga en Argentina. Sistema completo para instalar, reparar y diagnosticar aires y heladeras.');
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.href);
    // Preload hero image for better LCP
    const heroPreload = document.createElement('link');
    heroPreload.rel = 'preload';
    heroPreload.as = 'image';
    heroPreload.href = 'https://cdn.magicpatterns.com/uploads/jepLYJv1a4XGZs99UfsQ4i/refrigeracion_hero_mobile_768.webp';
    heroPreload.fetchPriority = 'high';
    document.head.appendChild(heroPreload);
    // Load fonts asynchronously (non-render-blocking)
    loadFontsAsync();
    // Defer Meta Pixel until after page is interactive
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(loadMetaPixelDeferred, {
        timeout: 3000
      });
    } else {
      setTimeout(loadMetaPixelDeferred, 2500);
    }
  }, []);
  return <div className="min-h-screen bg-brand-deep font-sans overflow-x-hidden" data-id="element-0">
      <StickyTopBar data-id="element-1" />
      <main data-id="element-2">
        <HeroSection data-id="element-3" />
        <SocialProofBar data-id="element-4" />
        <Suspense fallback={null} data-id="element-5">
          <ShortTestimonials data-id="element-6" />
          <PainPointsSection data-id="element-7" />
          <ProductShowcase data-id="element-8" />
          <TomosDetail data-id="element-9" />
          <IncludesSection data-id="element-10" />
          <BonusSection data-id="element-11" />
          <CountdownSection data-id="element-12" />
          <PricingSection data-id="element-13" />
          <FullTestimonials data-id="element-14" />
          <ForWhomSection data-id="element-15" />
          <GuaranteeSection data-id="element-16" />
          <FAQSection data-id="element-17" />
          <FinalCTASection data-id="element-18" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-19">
        <Footer data-id="element-20" />
        <SocialProofPopup data-id="element-21" />
      </Suspense>
    </div>;
}